import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Calculator, DollarSign, TrendingUp, Share2, Receipt } from "lucide-react";
import SeoHead from "@/components/shared/SeoHead";
import PageHero from "@/components/shared/PageHero";
import AdSlot from "@/components/shared/AdSlot";
import AdFrame from "@/components/shared/AdFrame";
import SalaryTable from "@/components/shared/SalaryTable";
import TaxCalculator from "@/components/shared/TaxCalculator";
import { PercentileIndicator } from "@/components/shared/PercentileIndicator";
import MetricNavigation from "@/components/shared/MetricNavigation";
import { supabase } from "@/integrations/supabase/client";
import { generateFAQPageSchema, generateMultipleSchemas, generateOrganizationSchema } from "@/lib/schema";

const SalaryCalculator = () => {
  const { amount } = useParams<{ amount?: string }>();
  const navigate = useNavigate();
  
  const [hourlyWage, setHourlyWage] = useState('');
  const [annualSalary, setAnnualSalary] = useState('');
  const [area, setArea] = useState('0000000'); // Default to national
  const [showTaxCalculator, setShowTaxCalculator] = useState(false);
  const [taxData, setTaxData] = useState(null);

  // Parse URL amount parameter with improved parsing for k suffix
  useEffect(() => {
    if (amount) {
      // Handle formats like 50k, $50k, 50000, $25-per-hour
      const cleanAmount = amount.replace(/[$,\-per\-hour]/g, '');
      
      if (amount.includes('hour') || amount.includes('hr')) {
        // It's an hourly wage
        const numericAmount = cleanAmount.includes('k') 
          ? parseFloat(cleanAmount.replace(/k/i, '')) * 1000 
          : parseFloat(cleanAmount);
        setHourlyWage(numericAmount.toString());
        setAnnualSalary('');
      } else {
        // It's an annual salary
        const numericAmount = cleanAmount.includes('k') 
          ? parseFloat(cleanAmount.replace(/k/i, '')) * 1000 
          : parseFloat(cleanAmount);
        setAnnualSalary(numericAmount.toString());
        setHourlyWage('');
      }
    }
  }, [amount]);

  const { data: calculatorData, isLoading } = useQuery({
    queryKey: ['salary-calculator', hourlyWage, annualSalary, area],
    queryFn: async () => {
      if (!hourlyWage && !annualSalary) return null;
      
      const { data, error } = await supabase.functions.invoke('api-calculator', {
        body: {
          hourly: hourlyWage ? parseFloat(hourlyWage) : 0,
          annual: annualSalary ? parseFloat(annualSalary) : 0,
          area: area,
          year: 2025
        }
      });
      
      if (error) throw error;
      return data;
    },
    enabled: !!(hourlyWage || annualSalary)
  });

  const handleHourlyChange = (value: string) => {
    setHourlyWage(value);
    setAnnualSalary('');
  };

  const handleAnnualChange = (value: string) => {
    setAnnualSalary(value);
    setHourlyWage('');
  };

  const handleShare = async () => {
    const wage = hourlyWage || (calculatorData?.calculations?.hourly_wage?.toString());
    const salary = annualSalary || (calculatorData?.calculations?.annual_salary?.toString());
    
    let shareUrl = window.location.origin;
    if (wage) {
      shareUrl += `/calculators/salary/$${wage}-per-hour`;
    } else if (salary) {
      shareUrl += `/calculators/salary/${Math.round(parseInt(salary) / 1000)}k`;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Wage Atlas Salary Calculator',
          text: `Check out these salary calculations for ${wage ? `$${wage}/hour` : `$${salary}/year`}`,
          url: shareUrl,
        });
      } catch (err) {
        // Fallback to copying to clipboard
        navigator.clipboard.writeText(shareUrl);
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Transform API data to match SalaryTable expectations
  const transformComparableJobs = (jobs: unknown[]) => {
    return jobs.map(job => {
      const annualMean = job.mean_salary || 0;
      const annualMedian = job.median_salary || 0;
      const hourlyMean = job.mean_salary ? Math.round(job.mean_salary / 2080 * 100) / 100 : 0;
      
      return {
        occ_title: job.occ_title || job.title || '',
        soc: job.occupation_code || job.soc || '',
        annual_mean_wage: annualMean,
        annual_median_wage: annualMedian,
        hourly_mean_wage: hourlyMean,
        total_employment: job.employment || 0,
      };
    });
  };

  const calculations = calculatorData?.calculations || null;
  const rawComparableJobs = calculatorData?.comparable_jobs || [];
  const comparableJobs = transformComparableJobs(rawComparableJobs);

  const pageTitle = amount 
    ? `${amount.replace(/-/g, ' ')} Salary Calculator - Annual & Hourly Wage Breakdown`
    : 'Salary Calculator - Convert Hourly to Annual Wage | Wage Atlas';

  const pageDescription = amount
    ? `Calculate annual salary, hourly wage, and find comparable jobs for ${amount.replace(/-/g, ' ')}. See wage percentiles and comparable occupations.`
    : 'Free salary calculator to convert between hourly and annual wages. Find comparable jobs and see your wage percentile with real-time data.';

  // Generate FAQ and HowTo schemas for AEO
  const baseUrl = 'https://wageatlas.com';
  const canonicalUrl = amount 
    ? `${baseUrl}/calculators/salary/${amount}/`
    : `${baseUrl}/calculators/salary/`;

  const faqItems = [
    {
      question: 'How do I convert hourly wage to annual salary?',
      answer: 'To convert hourly wage to annual salary, multiply your hourly rate by 2,080 (the number of working hours in a year for full-time work). For example, $25/hour × 2,080 hours = $52,000 per year. Our calculator does this automatically and accounts for overtime and part-time work.'
    },
    {
      question: 'How do I convert annual salary to hourly wage?',
      answer: 'To convert annual salary to hourly wage, divide your annual salary by 2,080 (standard working hours per year). For example, $52,000 ÷ 2,080 = $25 per hour. Our calculator handles this conversion and shows you comparable jobs at similar wage levels.'
    },
    {
      question: 'What is a wage percentile?',
      answer: 'A wage percentile shows where your salary falls compared to other workers. The 50th percentile is the median (half earn more, half earn less). The 75th percentile means you earn more than 75% of workers. Our calculator shows your percentile based on real Bureau of Labor Statistics data.'
    },
    {
      question: 'How accurate is the salary calculator?',
      answer: 'Our calculator uses official Bureau of Labor Statistics data from surveys of over 1.2 million businesses. The calculations are based on actual wages paid to workers, not estimates. However, individual salaries vary based on experience, education, company size, and location.'
    }
  ];

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Use the Salary Calculator',
    description: 'Step-by-step guide to converting between hourly and annual wages',
    step: [
      {
        '@type': 'HowToStep',
        name: 'Enter Your Wage',
        text: 'Enter either your hourly wage (e.g., $25.00) or annual salary (e.g., $52,000) in the calculator fields.'
      },
      {
        '@type': 'HowToStep',
        name: 'View Conversion',
        text: 'The calculator automatically converts between hourly and annual wages. You\'ll see both values displayed along with monthly, weekly, and daily breakdowns.'
      },
      {
        '@type': 'HowToStep',
        name: 'Check Your Percentile',
        text: 'See where your wage falls compared to other workers using percentile data from the Bureau of Labor Statistics.'
      },
      {
        '@type': 'HowToStep',
        name: 'Find Comparable Jobs',
        text: 'Browse similar occupations with comparable salary ranges to explore career alternatives or advancement opportunities.'
      }
    ]
  };

  const faqSchema = generateFAQPageSchema(faqItems);
  const organizationSchema = generateOrganizationSchema(baseUrl);
  const structuredData = generateMultipleSchemas([faqSchema, howToSchema, organizationSchema]);

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        occupation="Salary Calculator"
        salaryData={calculations ? {
          mean: calculations.annual_salary,
          median: calculations.annual_salary * 0.9,
          min: calculations.annual_salary * 0.7,
          max: calculations.annual_salary * 1.3
        } : undefined}
        canonical={canonicalUrl}
        schemas={structuredData}
      />

      {/* Hero Section */}
      <PageHero
        title="Salary Calculator"
        subtitle="Convert between hourly and annual wages, find comparable jobs"
      >
        {/* Top ad placement */}
        <AdFrame className="flex justify-center">
          <AdSlot slot="leaderboard" className="max-w-4xl" />
        </AdFrame>
      </PageHero>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Calculator Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Salary Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="hourly">Hourly Wage</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="hourly"
                        type="number"
                        placeholder="25.00"
                        value={hourlyWage}
                        onChange={(e) => handleHourlyChange(e.target.value)}
                        className="pl-10"
                        step="0.01"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Per hour</p>
                  </div>
                  <div>
                    <Label htmlFor="annual">Annual Salary</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="annual"
                        type="number"
                        placeholder="52000"
                        value={annualSalary}
                        onChange={(e) => handleAnnualChange(e.target.value)}
                        className="pl-10"
                        step="1000"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Per year</p>
                  </div>
                </div>

                {calculations && (
                  <div className="flex justify-end">
                    <Button onClick={handleShare} variant="outline" size="sm">
                      <Share2 className="h-4 w-4 mr-2" />
                      Share Results
                    </Button>
                  </div>
                )}

                {/* In-content ad */}
                <AdSlot slot="in-content" />
              </CardContent>
            </Card>

            {/* Results */}
            {calculations && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Calculation Results</CardTitle>
                  </CardHeader>
                  <CardContent>
                     <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                       <div className="text-center p-4 bg-wage-neutral rounded-lg">
                          <div className="text-lg md:text-2xl font-bold text-wage-primary">
                            {formatCurrency(calculations.hourly_wage)}
                          </div>
                         <div className="text-sm text-muted-foreground">Per Hour</div>
                       </div>
                       <div className="text-center p-4 bg-wage-neutral rounded-lg">
                          <div className="text-lg md:text-2xl font-bold text-wage-primary">
                            {formatCurrency(calculations.daily_wage || Math.round(calculations.hourly_wage * 8))}
                          </div>
                         <div className="text-sm text-muted-foreground">Per Day</div>
                       </div>
                       <div className="text-center p-4 bg-wage-neutral rounded-lg">
                          <div className="text-lg md:text-2xl font-bold text-wage-primary">
                            {formatCurrency(calculations.weekly_wage)}
                          </div>
                         <div className="text-sm text-muted-foreground">Per Week</div>
                       </div>
                       <div className="text-center p-4 bg-wage-neutral rounded-lg">
                          <div className="text-lg md:text-2xl font-bold text-wage-primary">
                            {formatCurrency(calculations.monthly_wage)}
                          </div>
                         <div className="text-sm text-muted-foreground">Per Month</div>
                       </div>
                       <div className="text-center p-4 bg-wage-neutral rounded-lg">
                          <div className="text-lg md:text-2xl font-bold text-wage-primary">
                            {formatCurrency(calculations.annual_salary)}
                          </div>
                         <div className="text-sm text-muted-foreground">Per Year</div>
                       </div>
                     </div>

                    {calculations.percentile > 0 && (
                      <div className="mt-6 p-6 bg-muted/50 rounded-lg border">
                        <PercentileIndicator 
                          percentile={calculations.percentile}
                          salary={calculations.annual_salary}
                        />
                      </div>
                    )}
                  </CardContent>
                 </Card>

                 {/* Tax Calculator Section */}
                 <div className="mb-6">
                   <TaxCalculator 
                     initialSalary={calculations?.annual_salary || 0}
                   />
                 </div>

                 {/* Comparable Jobs */}
                {comparableJobs.length > 0 && (
                  <SalaryTable
                    data={comparableJobs}
                    title="Jobs at Similar Pay Level"
                    showAffiliateEvery={6}
                    area={area}
                    className="shadow-sm"
                  />
                )}

                {/* Bottom content ad */}
                <AdSlot slot="in-content" />
              </>
            )}

            {/* Information */}
            <Card>
              <CardHeader>
                <CardTitle>How This Calculator Works</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
                <p>
                  This salary calculator uses real wage data from the Bureau of Labor Statistics to provide 
                  accurate conversions between hourly and annual wages, assuming a standard 40-hour work week 
                  (2,080 hours per year).
                </p>
                <Separator className="my-4" />
                <h4 className="font-semibold mb-2">Key Features:</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Comparable Jobs:</strong> Find other occupations at similar pay levels</li>
                  <li><strong>Percentile Ranking:</strong> See where your wage ranks among all jobs</li>
                  <li><strong>Multiple Timeframes:</strong> View hourly, weekly, monthly, and annual equivalents</li>
                  <li><strong>Accurate Calculations:</strong> Based on real wage data from the Bureau of Labor Statistics</li>
                </ul>
                <p className="mt-4">
                  Wage percentiles here compare occupations to each other. To see how a salary ranks
                  against all US earners, use the{' '}
                  <Link to="/income-percentile/" className="text-primary hover:underline underline-offset-2">
                    income percentile calculator
                  </Link>
                  .
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {comparableJobs.length > 0 && comparableJobs[0] && (
              <MetricNavigation
                occupation={{
                  occ_title: comparableJobs[0].occ_title,
                  soc: comparableJobs[0].soc,
                  soc_code: comparableJobs[0].soc,
                }}
                location="united-states"
                currentMetric="calculator"
                collapsible={true}
                defaultOpen={false}
              />
            )}
            
            {/* Sidebar Ad */}
            <AdSlot slot="sidebar" />

            {/* Quick Links - Hidden on tablet and mobile */}
            <Card className="hidden lg:block">
              <CardHeader>
                <CardTitle className="text-lg">Popular Calculations</CardTitle>
              </CardHeader>
               <CardContent className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/15-per-hour')}
                  >
                    $15 per hour
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/20-per-hour')}
                  >
                    $20 per hour
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/25-per-hour')}
                  >
                    $25 per hour
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/50-per-hour')}
                  >
                    $50 per hour
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/75-per-hour')}
                  >
                    $75 per hour
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/100-per-hour')}
                  >
                    $100 per hour
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/200-per-hour')}
                  >
                    $200 per hour
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/36k')}
                  >
                    $36k salary
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/50k')}
                  >
                    $50k salary
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/75k')}
                  >
                    $75k salary
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/100k')}
                  >
                    $100k salary
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/150k')}
                  >
                    $150k salary
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate('/calculators/salary/200k')}
                  >
                    $200k salary
                  </Button>
              </CardContent>
            </Card>

            {/* Mobile bottom ad */}
            <div className="lg:hidden">
              <AdSlot slot="mobile-banner" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryCalculator;